/**
 * `/gallery/live-state` shows every live game state the Scores row and game
 * detail can draw (ISSUE-1091 R7, D3), so a browser can measure them and a
 * reviewer can see them in both themes. It is signed in and linked from no
 * navigation, following the `/gallery/score-rows` precedent.
 *
 * A SEPARATE page on purpose. The card names `/gallery/score-rows` and
 * `/gallery/matchup-live`, but both are geometry fixtures for other cards'
 * e2e guards, which pin the exact rows and event ids they render. Adding
 * rows there would mean editing those guards to make room. The `/gallery`
 * index is captured as a visual baseline that only CI can refresh, so it is
 * left alone too.
 *
 * Every label below is exactly what `services/events-api/src/lib/liveStateWire.ts`
 * builds for that state. The app never builds them; the server does.
 *
 * Each state renders TWO rows: `live-<state>` with the state and `ctrl-<state>`
 * with the same game and `liveState: null`. `tests/e2e/src/app/liveStateGallery.e2e.ts`
 * compares the pair to prove a live state never moves the score or wraps a
 * team name (D8).
 *
 * The file is named `gallery_.live-state` so this route stays out of
 * `/gallery`'s tree: `/gallery` renders no Outlet, so a nested route would
 * never paint.
 */
import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import { LiveStatePanel } from "../features/liveState/LiveStatePanel";
import { LiveStateRowGlyph } from "../features/liveState/LiveStateRowGlyph";
import type { typeLiveStateWire } from "../features/liveState/liveStateTypes";
import { PossessionDot } from "../features/liveState/PossessionDot";
import { ScoreRow } from "../features/scores/ScoreRow";
import type { typeScoresEventInput } from "../features/scores/scoreRowModel";

type typeGalleryEvent = typeScoresEventInput & {
  liveState: typeLiveStateWire | null;
};

type typeSportGame = Pick<
  typeScoresEventInput,
  "statusLabel" | "home" | "away"
>;

const BASEBALL_GAME: typeSportGame = {
  statusLabel: "Inn 5",
  away: {
    teamId: "t-nyy",
    name: "New York Yankees",
    abbreviation: "NYY",
    score: 3,
  },
  home: {
    teamId: "t-lad",
    name: "Los Angeles Dodgers",
    abbreviation: "LAD",
    score: 2,
  },
};

const FOOTBALL_GAME: typeSportGame = {
  statusLabel: "Q3 · 0:19",
  away: { teamId: "t-mia", name: "Miami", abbreviation: "MIA", score: 24 },
  home: { teamId: "t-clem", name: "Clemson", abbreviation: "CLEM", score: 6 },
};

const BASKETBALL_GAME: typeSportGame = {
  statusLabel: "Q4 · 2:10",
  away: {
    teamId: "t-gsv",
    name: "Golden State Valkyries",
    abbreviation: "GSV",
    score: 71,
  },
  home: {
    teamId: "t-lynx",
    name: "Minnesota Lynx",
    abbreviation: "MIN",
    score: 84,
  },
};

const HOCKEY_GAME: typeSportGame = {
  statusLabel: "P2 · 8:12",
  away: {
    teamId: "t-sea",
    name: "Seattle Kraken",
    abbreviation: "SEA",
    score: 1,
  },
  home: {
    teamId: "t-wsh",
    name: "Washington Capitals",
    abbreviation: "WSH",
    score: 2,
  },
};

const baseball = (input: {
  balls: number;
  strikes: number;
  outs: number;
  isFirstOccupied: boolean;
  isSecondOccupied: boolean;
  isThirdOccupied: boolean;
  outsLabel: string;
  summaryLabel: string;
  pitcher?: { name: string; lineLabel: string | null };
  batter?: { name: string; lineLabel: string | null };
}): typeLiveStateWire => ({
  possessionTeamId: null,
  baseball: {
    balls: input.balls,
    strikes: input.strikes,
    outs: input.outs,
    isFirstOccupied: input.isFirstOccupied,
    isSecondOccupied: input.isSecondOccupied,
    isThirdOccupied: input.isThirdOccupied,
    countLabel: `${input.balls}-${input.strikes}`,
    outsLabel: input.outsLabel,
    pitcher: input.pitcher ?? null,
    batter: input.batter ?? null,
  },
  football: null,
  summaryLabel: input.summaryLabel,
});

type typeGalleryState = {
  id: string;
  title: string;
  game: typeSportGame;
  liveState: typeLiveStateWire | null;
};

/** Every R7 state, in the order the card lists them, then the no-data case. */
const LIVE_STATE_GALLERY_STATES: readonly typeGalleryState[] = [
  {
    id: "baseball-bases-empty-no-outs",
    title: "Baseball, bases empty, no outs",
    game: BASEBALL_GAME,
    liveState: baseball({
      balls: 0,
      strikes: 0,
      outs: 0,
      isFirstOccupied: false,
      isSecondOccupied: false,
      isThirdOccupied: false,
      outsLabel: "0 outs",
      summaryLabel: "Bases empty, 0 outs, count 0 and 0",
    }),
  },
  {
    id: "baseball-runner-first-one-out",
    title: "Baseball, runner on first, one out",
    game: BASEBALL_GAME,
    liveState: baseball({
      balls: 0,
      strikes: 1,
      outs: 1,
      isFirstOccupied: true,
      isSecondOccupied: false,
      isThirdOccupied: false,
      outsLabel: "1 out",
      summaryLabel: "Runner on first, 1 out, count 0 and 1",
    }),
  },
  {
    id: "baseball-bases-loaded-two-outs",
    title: "Baseball, bases loaded, two outs",
    game: BASEBALL_GAME,
    liveState: baseball({
      balls: 3,
      strikes: 2,
      outs: 2,
      isFirstOccupied: true,
      isSecondOccupied: true,
      isThirdOccupied: true,
      outsLabel: "2 outs",
      // ISSUE-1383. The pitcher and the batter with today's lines.
      pitcher: {
        name: "Clayton Kershaw",
        lineLabel: "IP 4.2 · H 3 · ER 1 · BB 1 · K 3",
      },
      batter: { name: "Aaron Judge", lineLabel: "0-1, K" },
      summaryLabel:
        "Bases loaded, 2 outs, count 3 and 2, Clayton Kershaw pitching to Aaron Judge",
    }),
  },
  {
    id: "football-ball-away-side",
    title: "Football, ball on the away side",
    game: FOOTBALL_GAME,
    liveState: {
      possessionTeamId: "t-mia",
      baseball: null,
      // ISSUE-1383. 1st & 10 with the drive just started on the Miami 15.
      football: {
        ballSpotLabel: "MIA 25",
        ballSpotPercentFromAway: 25,
        firstDownPercentFromAway: 35,
        driveStartPercentFromAway: 15,
        driveSummaryLabel: "2 plays · 10 yards · 0:48",
      },
      downAndDistanceLabel: "1st & 10",
      summaryLabel: "Miami ball on the Miami 25, 1st and 10",
    },
  },
  {
    id: "football-ball-home-side",
    title: "Football, ball on the home side",
    game: FOOTBALL_GAME,
    liveState: {
      possessionTeamId: "t-clem",
      baseball: null,
      football: { ballSpotLabel: "CLEM 30", ballSpotPercentFromAway: 70 },
      summaryLabel: "Clemson ball on the Clemson 30",
    },
  },
  {
    id: "football-midfield",
    title: "Football, ball at midfield",
    game: FOOTBALL_GAME,
    liveState: {
      possessionTeamId: "t-mia",
      baseball: null,
      football: { ballSpotLabel: "Midfield", ballSpotPercentFromAway: 50 },
      summaryLabel: "Miami ball at midfield",
    },
  },
  {
    id: "football-red-zone",
    title: "Football, red zone",
    game: FOOTBALL_GAME,
    liveState: {
      possessionTeamId: "t-mia",
      baseball: null,
      football: { ballSpotLabel: "CLEM 12", ballSpotPercentFromAway: 88 },
      summaryLabel: "Miami ball on the Clemson 12",
    },
  },
  {
    id: "football-possession-only",
    title: "Football, possession only",
    game: FOOTBALL_GAME,
    liveState: {
      possessionTeamId: "t-clem",
      baseball: null,
      football: null,
      summaryLabel: "Clemson has the ball",
    },
  },
  {
    id: "basketball-possession",
    title: "Basketball, possession",
    game: BASKETBALL_GAME,
    liveState: {
      possessionTeamId: "t-lynx",
      baseball: null,
      football: null,
      summaryLabel: "Minnesota Lynx has the ball",
    },
  },
  {
    id: "hockey-power-play",
    title: "Hockey, power play",
    game: HOCKEY_GAME,
    liveState: {
      possessionTeamId: null,
      baseball: null,
      football: null,
      hockey: { powerPlayTeamId: "t-wsh", powerPlayLabel: "PP" },
      summaryLabel: "Washington Capitals on the power play",
    },
  },
  {
    id: "no-data",
    title: "No live state",
    game: BASEBALL_GAME,
    liveState: null,
  },
];

const toEvent = (input: {
  eventId: string;
  game: typeSportGame;
  liveState: typeLiveStateWire | null;
}): typeGalleryEvent => ({
  eventId: input.eventId,
  status: "inProgress",
  statusLabel: input.game.statusLabel,
  isLive: true,
  winnerTeamId: null,
  isTie: false,
  away: input.game.away,
  home: input.game.home,
  liveState: input.liveState,
});

const GALLERY_ROWS: readonly typeGalleryEvent[] =
  LIVE_STATE_GALLERY_STATES.flatMap((state) => [
    toEvent({
      eventId: `live-${state.id}`,
      game: state.game,
      liveState: state.liveState,
    }),
    toEvent({
      eventId: `ctrl-${state.id}`,
      game: state.game,
      liveState: null,
    }),
  ]);

/** This page has nowhere to navigate to; the callbacks only make the row's
 *  targets the `<button>`s a shipped row has (ISSUE-980). */
const openNothing = (): void => undefined;

const plateTextOf = (team: typeSportGame["home"]): string => team.abbreviation;

/** True when the state draws something beyond the possession dot. */
const hasGlyph = (liveState: typeLiveStateWire | null): boolean =>
  liveState !== null &&
  ((liveState.baseball ?? null) !== null ||
    (liveState.football ?? null) !== null);

const LiveStateGalleryScreen = (): ReactNode => (
  <div className="flex flex-col gap-6 px-page-gutter py-4">
    <h1 className="text-h1 text-ink">Live state gallery</h1>
    <p className="text-body text-ink-secondary">
      Every live game state the Scores row and game detail can draw, so a
      browser can measure them. It is not linked from any navigation.
    </p>

    <section
      data-testid="live-state-gallery-rows"
      className="flex flex-col gap-2"
    >
      <h2 className="text-h2 text-ink">Score rows</h2>
      {/* LeagueSectionCard's ready-frame wrapper, class for class. */}
      <div className="overflow-hidden rounded-md border border-hairline bg-surface">
        {GALLERY_ROWS.map((event) => (
          <ScoreRow
            key={event.eventId}
            event={event}
            onOpenGame={openNothing}
          />
        ))}
      </div>
    </section>

    <section
      data-testid="live-state-gallery-glyphs"
      className="flex flex-col gap-2"
    >
      <h2 className="text-h2 text-ink">Row glyphs</h2>
      {LIVE_STATE_GALLERY_STATES.filter((state) =>
        hasGlyph(state.liveState),
      ).map((state) => (
        <div
          key={state.id}
          data-testid="live-state-gallery-glyph"
          data-state={state.id}
          className="flex flex-wrap items-start gap-4"
        >
          {/* The 60px content box of the in-play rail, then the 128px content
              box of the sm status column. */}
          <div className="w-15 shrink-0">
            {state.liveState !== null ? (
              <LiveStateRowGlyph liveState={state.liveState} />
            ) : null}
          </div>
          <div className="w-32 shrink-0">
            {state.liveState !== null ? (
              <LiveStateRowGlyph liveState={state.liveState} />
            ) : null}
          </div>
          {(state.liveState?.possessionTeamId ?? null) !== null ? (
            <PossessionDot />
          ) : null}
          <span className="text-caption text-ink-secondary">{state.title}</span>
        </div>
      ))}
    </section>

    <section
      data-testid="live-state-gallery-panels"
      className="flex flex-col gap-4"
    >
      <h2 className="text-h2 text-ink">Game detail</h2>
      {LIVE_STATE_GALLERY_STATES.map((state) => (
        <div
          key={state.id}
          data-testid="live-state-gallery-panel"
          data-state={state.id}
          className="flex max-w-xl flex-col gap-2"
        >
          <h3 className="text-body-strong text-ink">{state.title}</h3>
          {state.liveState === null ? (
            <p className="text-caption text-ink-secondary">
              Nothing is drawn when the server sends no live state.
            </p>
          ) : (state.liveState.hockey ?? null) !== null ? (
            <p className="text-caption text-ink-secondary">
              Power play: the row and the header draw the PP badge, Matchup
              draws no panel.
            </p>
          ) : !hasGlyph(state.liveState) ? (
            <p className="text-caption text-ink-secondary">
              Possession only: the header draws the dot, Matchup draws no panel.
            </p>
          ) : (
            <LiveStatePanel
              liveState={state.liveState}
              awayPlateText={plateTextOf(state.game.away)}
              homePlateText={plateTextOf(state.game.home)}
            />
          )}
        </div>
      ))}
    </section>
  </div>
);

export const Route = createFileRoute("/gallery_/live-state")({
  beforeLoad: requireSignedIn,
  component: LiveStateGalleryScreen,
});
